/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobLogs.tsx
 *     Feature         :   114-job-infrastructure
 *     Description     :   Terminal-style run log viewer — continuous monospace stream
 *                         (timestamp + severity-colored message), Top/Latest/Copy/Refresh,
 *                         Errors toggle, click-to-filter level counts + duration footer.
 *                         Adapted from the DevHub "Fabric Control — Logs" viewer; replaces
 *                         the prior heavy MUI table + paginator.
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28 | Rebuilt 2026-06-09
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { clsx } from 'clsx';
import { Search, RefreshCw, X, ArrowUpToLine, ArrowDownToLine, Copy } from 'lucide-react';
import { format } from 'date-fns';

import { Alert, Button, Input, AppTooltip, Spinner } from '../../components/ui';
import { getJobRunLogs, JobLog, LogSeverity, JobLogFilters } from '../../services/jobService';

/** Most recent N lines fetched in one shot for the stream view. */
const MAX_LINES = 2000;

/** Severity → message color (token-driven status colors; DEBUG = secondary text). */
const LEVEL_COLOR: Record<LogSeverity, string> = {
  DEBUG: 'var(--color-text-secondary)',
  INFO: 'var(--color-text-primary)',
  WARN: 'var(--color-status-warning)',
  ERROR: 'var(--color-status-danger)',
  FATAL: 'var(--color-status-danger)',
};

interface JobLogsProps {
  jobRunId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

/** Icon-button styling (token-driven; replaces MUI IconButton). */
const iconBtn =
  'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 ' +
  'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]';

/** A clickable level count in the footer (toggles the active filter). */
const LevelStat: React.FC<{
  label: string;
  count: number;
  color?: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, color, active, onClick }) => (
  <span
    onClick={onClick}
    style={{ color: color ?? 'var(--color-text-secondary)' }}
    className={clsx(
      'cursor-pointer whitespace-nowrap rounded px-1.5 py-0.5',
      active ? 'bg-[var(--color-bg-subtle)]' : 'hover:bg-[var(--color-bg-subtle)]',
    )}
  >
    <strong>{count.toLocaleString()}</strong> {label}
  </span>
);

export const JobLogs: React.FC<JobLogsProps> = ({
  jobRunId,
  autoRefresh = false,
  refreshInterval = 5000,
}) => {
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState<LogSeverity | 'ERRORS' | 'ALL'>('ALL');
  const bodyRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: JobLogFilters = { page: 1, pageSize: MAX_LINES };
      const res = await getJobRunLogs(jobRunId, filters);
      setLogs(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [jobRunId]);

  useEffect(() => {
    fetchLogs();
    if (autoRefresh) {
      const t = setInterval(fetchLogs, refreshInterval);
      return () => clearInterval(t);
    }
    return undefined;
  }, [fetchLogs, autoRefresh, refreshInterval]);

  // Level counts over the full fetched set.
  const counts = useMemo(() => {
    const c: Record<LogSeverity, number> = { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0, FATAL: 0 };
    logs.forEach((l) => { c[l.severity] = (c[l.severity] ?? 0) + 1; });
    return c;
  }, [logs]);
  const errorTotal = counts.ERROR + counts.FATAL;

  // Client-side filter (level + search) — instant, no re-fetch.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      const levelOk =
        level === 'ALL' ||
        (level === 'ERRORS' ? l.severity === 'ERROR' || l.severity === 'FATAL' : l.severity === level);
      const searchOk = !q || l.message.toLowerCase().includes(q);
      return levelOk && searchOk;
    });
  }, [logs, level, search]);

  // Auto-scroll to the latest line whenever logs (re)load.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [logs]);

  const fmtTs = (t: string) => {
    try { return format(new Date(t), 'MM-dd HH:mm:ss.SSS'); } catch { return t; }
  };

  const scrollTop = () => { if (bodyRef.current) bodyRef.current.scrollTop = 0; };
  const scrollBottom = () => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; };
  const copyLogs = () => {
    const text = visible.map((l) => `${fmtTs(l.timestamp)}  [${l.severity}] ${l.message}`).join('\n');
    navigator.clipboard?.writeText(text);
  };

  const duration = useMemo(() => {
    if (logs.length < 2) return null;
    const a = new Date(logs[0].timestamp).getTime();
    const b = new Date(logs[logs.length - 1].timestamp).getTime();
    const ms = Math.abs(b - a);
    if (!isFinite(ms)) return null;
    return ms > 60000
      ? `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
      : `${Math.round(ms / 1000)}s`;
  }, [logs]);

  const toggle = (lv: LogSeverity | 'ERRORS') => setLevel((cur) => (cur === lv ? 'ALL' : lv));

  return (
    <div className="flex min-h-[380px] flex-col" style={{ height: '62vh' }}>
      {/* Toolbar */}
      <div className="mb-2 flex flex-wrap items-center gap-1">
        <div className="relative min-w-[200px] flex-grow">
          <Input
            inputSize="sm"
            placeholder="Filter logs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            isFullWidth
            leftAddon={<Search className="h-4 w-4" />}
            rightAddon={
              search ? (
                <button className={iconBtn} onClick={() => setSearch('')}>
                  <X className="h-4 w-4" />
                </button>
              ) : undefined
            }
          />
        </div>
        <AppTooltip title="Scroll to top">
          <button className={iconBtn} onClick={scrollTop}><ArrowUpToLine className="h-4 w-4" /></button>
        </AppTooltip>
        <AppTooltip title="Scroll to latest">
          <button className={iconBtn} onClick={scrollBottom}><ArrowDownToLine className="h-4 w-4" /></button>
        </AppTooltip>
        <AppTooltip title="Copy visible logs">
          <button className={iconBtn} onClick={copyLogs}><Copy className="h-4 w-4" /></button>
        </AppTooltip>
        <Button
          size="sm"
          variant={level === 'ERRORS' ? 'danger' : 'outline'}
          onClick={() => toggle('ERRORS')}
          className="whitespace-nowrap normal-case"
        >
          Errors ({errorTotal})
        </Button>
        <AppTooltip title="Reload logs">
          <button className={iconBtn} onClick={fetchLogs} disabled={loading}><RefreshCw className="h-4 w-4" /></button>
        </AppTooltip>
      </div>

      {error && <Alert variant="error" className="mb-1">{error}</Alert>}

      {/* Log stream */}
      <div
        ref={bodyRef}
        className="flex-1 overflow-auto rounded-[var(--radius-md)] bg-[var(--color-bg-sunken)] p-2 leading-[1.55]"
        style={{
          color: 'var(--color-text-primary)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: '0.78rem',
        }}
      >
        {loading && logs.length === 0 ? (
          <div className="flex justify-center py-6">
            <Spinner size="md" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-6 text-center text-[var(--color-text-secondary)]">
            {logs.length === 0 ? 'No logs for this run yet.' : 'No lines match the current filter.'}
          </div>
        ) : (
          visible.map((l) => (
            <div
              key={l.id}
              className="flex gap-3 whitespace-pre-wrap break-words px-0.5 hover:bg-[var(--color-bg-subtle)]"
            >
              <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0, userSelect: 'none' }}>{fmtTs(l.timestamp)}</span>
              <span style={{ color: LEVEL_COLOR[l.severity] }}>{l.message}</span>
            </div>
          ))
        )}
      </div>

      {/* Footer — line/level counts (click to filter) + duration */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-secondary)]">
        <LevelStat label="lines" count={visible.length} active={level === 'ALL'} onClick={() => setLevel('ALL')} />
        {errorTotal > 0 && (
          <LevelStat label="errors" count={errorTotal} color={LEVEL_COLOR.ERROR} active={level === 'ERRORS'} onClick={() => toggle('ERRORS')} />
        )}
        {counts.WARN > 0 && (
          <LevelStat label="warnings" count={counts.WARN} color={LEVEL_COLOR.WARN} active={level === 'WARN'} onClick={() => toggle('WARN')} />
        )}
        {counts.INFO > 0 && (
          <LevelStat label="info" count={counts.INFO} active={level === 'INFO'} onClick={() => toggle('INFO')} />
        )}
        {counts.DEBUG > 0 && (
          <LevelStat label="debug" count={counts.DEBUG} active={level === 'DEBUG'} onClick={() => toggle('DEBUG')} />
        )}
        <span className="ml-0.5 text-[var(--color-text-secondary)] opacity-70">
          {level === 'ALL' && !search ? 'click to filter' : `showing ${visible.length} of ${logs.length}`}
        </span>
        {logs.length >= MAX_LINES && (
          <span style={{ color: 'var(--color-status-warning)' }}>· first {MAX_LINES} lines only</span>
        )}
        {duration && (
          <span className="ml-auto">Duration: <strong>{duration}</strong></span>
        )}
      </div>
    </div>
  );
};

export default JobLogs;
